import React, { useState, useEffect } from "react";
import { 
  makeStyles, 
  TextField, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Switch, 
  FormControlLabel, 
  Button, 
  Chip,
  Box,
  Divider,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup
} from "@material-ui/core";
import { Formik, FastField, FieldArray } from "formik";
import { isArray } from "lodash";
import NumberFormat from "react-number-format";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { i18n } from "../../translate/i18n";
import WbSunnyIcon from '@material-ui/icons/WbSunny';
import Brightness3Icon from '@material-ui/icons/Brightness3';
import BusinessIcon from '@material-ui/icons/Business';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import Schedule from '@material-ui/icons/Schedule';
import EditIcon from '@material-ui/icons/Edit';


const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  fullWidth: {
    width: "100%",
  },
  textfield: {
    width: "100%",
    fontSize: "0.875em"
  },
  row: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  control: {
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
  buttonContainer: {
    textAlign: "right",
    padding: theme.spacing(1),
  },
  dayCard: {
    marginBottom: theme.spacing(2),
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[4],
    }
  },
  activeCard: {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  inactiveCard: {
    borderLeft: (theme.palette.type === 'dark' || theme.palette.mode === 'dark')
      ? `4px solid ${theme.palette.grey[600]}`
      : `4px solid ${theme.palette.grey[300]}`,
    opacity: 0.7,
  },
  dayHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  dayTitle: {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  periodContainer: {
    padding: theme.spacing(1),
    backgroundColor: (theme.palette.type === 'dark' || theme.palette.mode === 'dark') 
      ? theme.palette.grey[800] 
      : theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  timeInputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  templateButtons: {
    marginBottom: theme.spacing(3),
    '& > *': {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
    }
  },
  periodTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  iconMorning: {
    color: '#FF9800', // Orange for morning
  },
  iconAfternoon: {
    color: '#3F51B5', // Blue for afternoon
  },
  timeField: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.grey[300],
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
    }
  }
}));

function SchedulesForm(props) {
  const { initialValues, onSubmit, loading, labelSaveButton } = props;
  const classes = useStyles();

  const [schedules, setSchedules] = useState([
    { weekday: i18n.t("queueModal.serviceHours.monday"), weekdayEn: "monday", startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: "", },
    { weekday: i18n.t("queueModal.serviceHours.tuesday"), weekdayEn: "tuesday", startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: "", },
    { weekday: i18n.t("queueModal.serviceHours.wednesday"), weekdayEn: "wednesday", startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: "", },
    { weekday: i18n.t("queueModal.serviceHours.thursday"), weekdayEn: "thursday", startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: "", },
    { weekday: i18n.t("queueModal.serviceHours.friday"), weekdayEn: "friday", startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: "", },
    { weekday: i18n.t("queueModal.serviceHours.saturday"), weekdayEn: "saturday", startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: "", },
    { weekday: i18n.t("queueModal.serviceHours.sunday"), weekdayEn: "sunday", startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: "", },
  ]);

  // Estados para o template personalizado
  const [customTemplateOpen, setCustomTemplateOpen] = useState(false);
  const [customTemplate, setCustomTemplate] = useState({
    startTimeA: "08:00",
    endTimeA: "12:00", 
    startTimeB: "13:00",
    endTimeB: "18:00",
    selectedDays: {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false
    }
  });

  // Templates predefinidos
  const templates = {
    comercial: {
      name: "Comercial",
      icon: <BusinessIcon />,
      schedule: {
        startTimeA: "08:00", endTimeA: "12:00",
        startTimeB: "13:00", endTimeB: "18:00"
      },
      days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
    },
    plantao: {
      name: "Plantão",
      icon: <LocalHospitalIcon />,
      schedule: {
        startTimeA: "08:00", endTimeA: "20:00",
        startTimeB: "", endTimeB: ""
      },
      days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
    },
    continuo: {
      name: "24h",
      icon: <Schedule />,
      schedule: {
        startTimeA: "00:00", endTimeA: "23:59",
        startTimeB: "", endTimeB: ""
      },
      days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
    }
  };

  useEffect(() => {
    if (isArray(initialValues) && initialValues.length > 0) {
      setSchedules(initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const handleSubmit = (data, formikBag) => {
    onSubmit(data, formikBag);
  };

  const applyTemplate = (templateKey, setFieldValue) => {
    const template = templates[templateKey];
    if (!template) return;

    const newSchedules = schedules.map(day => {
      if (template.days.includes(day.weekdayEn)) {
        return {
          ...day,
          ...template.schedule
        };
      } else {
        return {
          ...day,
          startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: ""
        };
      }
    });

    setSchedules(newSchedules);
    setFieldValue('schedules', newSchedules);
  };

  const applyCustomTemplate = (setFieldValue) => {
    const selectedDaysArray = Object.entries(customTemplate.selectedDays)
      .filter(([day, selected]) => selected)
      .map(([day]) => day);

    const newSchedules = schedules.map(day => {
      if (selectedDaysArray.includes(day.weekdayEn)) {
        return {
          ...day,
          startTimeA: customTemplate.startTimeA,
          endTimeA: customTemplate.endTimeA,
          startTimeB: customTemplate.startTimeB,
          endTimeB: customTemplate.endTimeB
        };
      } else {
        return day; // Mantém os horários existentes para dias não selecionados
      }
    });

    setSchedules(newSchedules);
    setFieldValue('schedules', newSchedules);
    setCustomTemplateOpen(false);
  };

  const handleCustomTemplateChange = (field, value) => {
    setCustomTemplate(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDaySelectionChange = (day, checked) => {
    setCustomTemplate(prev => ({
      ...prev,
      selectedDays: {
        ...prev.selectedDays,
        [day]: checked
      }
    }));
  };

  const selectAllDays = () => {
    setCustomTemplate(prev => ({
      ...prev,
      selectedDays: {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: true,
        sunday: true
      }
    }));
  };

  const selectWorkDays = () => {
    setCustomTemplate(prev => ({
      ...prev,
      selectedDays: {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: false,
        sunday: false
      }
    }));
  };

  const selectWeekend = () => {
    setCustomTemplate(prev => ({
      ...prev,
      selectedDays: {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: true,
        sunday: true
      }
    }));
  };

  const isDayActive = (day) => {
    return day.startTimeA || day.endTimeA || day.startTimeB || day.endTimeB;
  };

  const isPeriodActive = (day, period) => {
    if (period === 'A') {
      return day.startTimeA && day.endTimeA;
    } else {
      return day.startTimeB && day.endTimeB;
    }
  };

  return (
    <Formik
      enableReinitialize
      className={classes.fullWidth}
      initialValues={{ schedules }}
      onSubmit={({ schedules }, formikBag) => {
        // Previne o reload da página
        setTimeout(() => {
          handleSubmit(schedules, formikBag);
        }, 500);
      }}
    >
      {({ values, handleSubmit: formikHandleSubmit, setFieldValue }) => (
        <div className={classes.fullWidth}>
          {/* Templates Predefinidos */}
          <Box className={classes.templateButtons}>
            <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600 }}>
              ⚡ Templates Rápidos
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Aplique configurações predefinidas para agilizar o processo:
            </Typography>
            {Object.entries(templates).map(([key, template]) => (
              <Button
                key={key}
                variant="outlined"
                size="small"
                startIcon={template.icon}
                onClick={() => applyTemplate(key, setFieldValue)}
                style={{ textTransform: 'none', marginRight: 8, marginBottom: 8 }}
              >
                {template.name}
              </Button>
            ))}
            <Button
              variant="outlined"
              size="small"
              color="primary"
              startIcon={<EditIcon />}
              onClick={() => setCustomTemplateOpen(true)}
              style={{ textTransform: 'none', marginRight: 8, marginBottom: 8 }}
            >
              ✏️ Personalizado
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              onClick={() => {
                const emptySchedules = schedules.map(day => ({
                  ...day,
                  startTimeA: "", endTimeA: "", startTimeB: "", endTimeB: ""
                }));
                setSchedules(emptySchedules);
                setFieldValue('schedules', emptySchedules);
              }}
              style={{ textTransform: 'none', marginRight: 8, marginBottom: 8 }}
            >
              🗑️ Limpar Tudo
            </Button>
          </Box>

          <FieldArray
            name="schedules"
            render={(arrayHelpers) => (
              <Grid spacing={2} container>
                {values.schedules.map((item, index) => {
                  const isActive = isDayActive(item);
                  return (
                    <Grid key={index} xs={12} md={6} lg={4} item>
                      <Card 
                        className={`${classes.dayCard} ${isActive ? classes.activeCard : classes.inactiveCard}`}
                        elevation={isActive ? 3 : 1}
                      >
                        <CardContent>
                          {/* Cabeçalho do Dia */}
                          <div className={classes.dayHeader}>
                            <Typography variant="h6" className={classes.dayTitle}>
                              {item.weekday}
                            </Typography>
                            {isActive && (
                              <Chip 
                                label="Ativo" 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                            )}
                          </div>

                          {/* Período da Manhã */}
                          <Box className={classes.periodContainer}>
                            <div className={classes.periodTitle}>
                              <WbSunnyIcon className={classes.iconMorning} />
                              <Typography variant="subtitle2">
                                Manhã
                              </Typography>
                              {isPeriodActive(item, 'A') && (
                                <Chip 
                                  label={`${item.startTimeA} - ${item.endTimeA}`} 
                                  size="small" 
                                  color="primary"
                                />
                              )}
                            </div>
                            
                            <div className={classes.timeInputContainer}>
                              <FastField
                                label="Início"
                                name={`schedules[${index}].startTimeA`}
                              >
                                {({ field }) => (
                                  <NumberFormat
                                    {...field}
                                    variant="outlined"
                                    margin="dense"
                                    size="small"
                                    customInput={TextField}
                                    format="##:##"
                                    placeholder="08:00"
                                    className={classes.timeField}
                                    style={{ width: '80px' }}
                                  />
                                )}
                              </FastField>
                              
                              <Typography variant="body2">até</Typography>
                              
                              <FastField
                                label="Fim"
                                name={`schedules[${index}].endTimeA`}
                              >
                                {({ field }) => (
                                  <NumberFormat
                                    {...field}
                                    variant="outlined"
                                    margin="dense"
                                    size="small"
                                    customInput={TextField}
                                    format="##:##"
                                    placeholder="12:00"
                                    className={classes.timeField}
                                    style={{ width: '80px' }}
                                  />
                                )}
                              </FastField>
                            </div>
                          </Box>

                          {/* Período da Tarde */}
                          <Box className={classes.periodContainer}>
                            <div className={classes.periodTitle}>
                              <Brightness3Icon className={classes.iconAfternoon} />
                              <Typography variant="subtitle2">
                                Tarde
                              </Typography>
                              {isPeriodActive(item, 'B') && (
                                <Chip 
                                  label={`${item.startTimeB} - ${item.endTimeB}`} 
                                  size="small" 
                                  color="primary"
                                />
                              )}
                            </div>
                            
                            <div className={classes.timeInputContainer}>
                              <FastField
                                label="Início"
                                name={`schedules[${index}].startTimeB`}
                              >
                                {({ field }) => (
                                  <NumberFormat
                                    {...field}
                                    variant="outlined"
                                    margin="dense"
                                    size="small"
                                    customInput={TextField}
                                    format="##:##"
                                    placeholder="13:00"
                                    className={classes.timeField}
                                    style={{ width: '80px' }}
                                  />
                                )}
                              </FastField>
                              
                              <Typography variant="body2">até</Typography>
                              
                              <FastField
                                label="Fim"
                                name={`schedules[${index}].endTimeB`}
                              >
                                {({ field }) => (
                                  <NumberFormat
                                    {...field}
                                    variant="outlined"
                                    margin="dense"
                                    size="small"
                                    customInput={TextField}
                                    format="##:##"
                                    placeholder="18:00"
                                    className={classes.timeField}
                                    style={{ width: '80px' }}
                                  />
                                )}
                              </FastField>
                            </div>
                          </Box>

                          {/* Campo oculto para o dia da semana */}
                          <FastField
                            name={`schedules[${index}].weekday`}
                            type="hidden"
                          />
                          <FastField
                            name={`schedules[${index}].weekdayEn`}
                            type="hidden"
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          ></FieldArray>
          
          <Divider style={{ margin: '20px 0' }} />
          
          <div className={classes.buttonContainer}>
            <ButtonWithSpinner
              loading={loading}
              onClick={(e) => {
                e.preventDefault();
                formikHandleSubmit();
              }}
              color="primary"
              variant="contained"
              size="large"
            >
              {labelSaveButton ?? i18n.t("whatsappModal.buttons.okEdit")}
            </ButtonWithSpinner>
          </div>

          {/* Modal Template Personalizado */}
          <Dialog
            open={customTemplateOpen}
            onClose={() => setCustomTemplateOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              ✏️ Template Personalizado
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Configure horários personalizados e escolha em quais dias aplicar:
              </Typography>
              
              {/* Configuração de Horários */}
              <Box style={{ marginTop: 20, marginBottom: 20 }}>
                <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                  🕐 Configurar Horários
                </Typography>
                
                <Grid container spacing={2} style={{ marginBottom: 16 }}>
                  <Grid item xs={12}>
                    <Typography variant="body2" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <WbSunnyIcon style={{ color: '#FF9800' }} />
                      <Typography component="span" style={{ fontWeight: 600 }}>Período da Manhã:</Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <NumberFormat
                      value={customTemplate.startTimeA}
                      onValueChange={(values) => handleCustomTemplateChange('startTimeA', values.formattedValue)}
                      format="##:##"
                      customInput={TextField}
                      variant="outlined"
                      size="small"
                      label="Início"
                      placeholder="08:00"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <NumberFormat
                      value={customTemplate.endTimeA}
                      onValueChange={(values) => handleCustomTemplateChange('endTimeA', values.formattedValue)}
                      format="##:##"
                      customInput={TextField}
                      variant="outlined"
                      size="small"
                      label="Fim"
                      placeholder="12:00"
                      fullWidth
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Brightness3Icon style={{ color: '#3F51B5' }} />
                      <Typography component="span" style={{ fontWeight: 600 }}>Período da Tarde:</Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <NumberFormat
                      value={customTemplate.startTimeB}
                      onValueChange={(values) => handleCustomTemplateChange('startTimeB', values.formattedValue)}
                      format="##:##"
                      customInput={TextField}
                      variant="outlined"
                      size="small"
                      label="Início"
                      placeholder="13:00"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <NumberFormat
                      value={customTemplate.endTimeB}
                      onValueChange={(values) => handleCustomTemplateChange('endTimeB', values.formattedValue)}
                      format="##:##"
                      customInput={TextField}
                      variant="outlined"
                      size="small"
                      label="Fim"
                      placeholder="18:00"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Seleção de Dias */}
              <Box style={{ marginTop: 20 }}>
                <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                  📅 Selecionar Dias
                </Typography>
                
                <Box style={{ marginBottom: 16 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={selectAllDays}
                    style={{ marginRight: 8, textTransform: 'none' }}
                  >
                    Todos os dias
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={selectWorkDays}
                    style={{ marginRight: 8, textTransform: 'none' }}
                  >
                    Dias úteis
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={selectWeekend}
                    style={{ textTransform: 'none' }}
                  >
                    Fim de semana
                  </Button>
                </Box>

                <FormGroup row>
                  {schedules.map((day) => (
                    <FormControlLabel
                      key={day.weekdayEn}
                      control={
                        <Checkbox
                          checked={customTemplate.selectedDays[day.weekdayEn] || false}
                          onChange={(e) => handleDaySelectionChange(day.weekdayEn, e.target.checked)}
                          color="primary"
                        />
                      }
                      label={day.weekday}
                    />
                  ))}
                </FormGroup>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setCustomTemplateOpen(false)}
                color="secondary"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => applyCustomTemplate(setFieldValue)}
                color="primary"
                variant="contained"
                disabled={Object.values(customTemplate.selectedDays).every(selected => !selected)}
              >
                Aplicar aos Dias Selecionados
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </Formik>
  );
}

export default SchedulesForm;
